import dayjs from "dayjs";

import {getValidAccessToken} from "./Utils/Auth";

export const getPlatformUrl = (environment?: string) => {
  const env = environment || localStorage.getItem("env");
  const suffix =
    !env || env === "PROD"
      ? ""
      : env === "DEV"
        ? ".develop"
        : `.${env.toLowerCase()}`;
  return `https://api${suffix}.leanspace.io`;
}

export const getToken = async () => {
  return getValidAccessToken();
};

export const formatDate = (value: any) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : String(value);
};

export const formatDateTime = (value: any) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY, HH:mm") : String(value);
};

export const formatJwt = (jwt: string) => {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    return {
      isJwt: false,
      header: null,
      payload: null,
      signature: null,
      raw: jwt,
    };
  }

  const [h, p, s] = parts;
  const headerRaw = decodeBase64Url(h);
  const payloadRaw = decodeBase64Url(p);

  return {
    isJwt: true,
    header: JSON.stringify(JSON.parse(headerRaw), null, 2),
    payload: JSON.stringify(JSON.parse(payloadRaw), null, 2),
    signature: s,
    raw: jwt,
  };
};

export const decodeBase64Url = (input: string) => {
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "===".slice((base64.length + 3) % 4);
  const decoded = atob(padded);
  // Handle UTF-8
  const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
  return new TextDecoder("utf-8").decode(bytes);
};

type ServiceGroupsBlueprint = {
  Activities: string[];
  Commands: string[];
  Console: string[];
  Constraints: string[];
  Dashboards: string[];
  Events: string[];
  Files: string[];
  Infrastructure: string[];
  Integrations: string[];
  "Integration-testing": string[];
  Jobs: string[];
  Kafka: string[];
  Metrics: string[];
  Monitors: string[];
  Orbits: string[];
  Passes: string[];
  Plans: string[];
  Procedures: string[];
  Plugins: string[];
  Records: string[];
  Resources: string[];
  Route: string[];
  Requirements: string[];
  Requests: string[];
  Streams: string[];
  Synoptics: string[];
  "Teams and Roles": string[];
  Tenant: string[];
  "Tenant Data Usage": string[];
  Topology: string[];
  Ungrouped: string[];
};

export const serviceGroupsBlueprint: ServiceGroupsBlueprint = {
  Activities: ["activities-repository"],
  Commands: ["commands-repository"],
  Console: ["console"],
  Constraints: ["constraints-repository"],
  Dashboards: ["dashboard-repository"],
  Events: [
    "events-repository",
    "events-service-bus-lambda",
    "events-stream-lambda",
    "messaging-authorizer-repository",
  ],
  Infrastructure: [
    "api-gateway-authorizer",
    "leanspace-infra",
    "leanspace-logforwarder-image",
    "leanspace-opensearch-snapshot-lambda",
    "messaging-authorizer-repository",
    "rate-limiting-lambda",
    "feature-enablement-lambda",
    "keycloak-image"
  ],
  Integrations: [
    "integration-leafspace-repository",
    "valispace-integrations-repository",
    "integration-exotrail-repository",
  ],
  Jobs: ["jobs-repository", "jobs-poc"],
  "Integration-testing": ["integration-testing"],
  Kafka: ["kafka-authorizer-repository", "kafka-base-image"],
  Files: ["files-repository"],
  Metrics: [
    "lambda-metrics-ingestion-processing",
    "lambda-telemetry-metrics-ingestion-forwarder",
    "metrics-lambda-ingestion-persistence-support",
    "metrics-repository",
  ],
  Monitors: [
    "monitors-data-processing-lambda",
    "monitors-evaluation-splitter-lambda",
    "monitors-live-evaluation-lambda",
    "monitors-notifications-lambda",
    "monitors-repository",
  ],
  Orbits: [
    "orbits-ephemeris-ingestion-lambda",
    "orbits-propagations-and-events-generation-lambda",
    "orbits-repository",
  ],
  Passes: ["passes-repository"],
  Plans: ["plans-repository"],
  Plugins: ["plugins-repository"],
  Procedures: ["procedures-repository"],
  Records: ["records-repository", "records-upload-processing-lambda"],
  Requests: ["requests-repository"],
  Requirements: ["requirements-repository"],
  Resources: ["resources-repository"],
  Route: ["leanspace-route", "routes-repository"],
  Streams: [
    "lambda-packet-decoder",
    "streams-lambda-ingestion-persistence-support",
    "streams-repository",
  ],
  Synoptics: ["synoptics-repository"],
  "Teams and Roles": ["teams-repository", "leanspace-open-policy-agent-image"],
  Tenant: ["tenant-repository", "tenants-update-required"],
  "Tenant Data Usage": ["data-usage-monitoring-lambda"],
  Topology: ["asset-repository"],
  Ungrouped: [
    // all repositories that are not in the above groups will be put here
    // if you see a repo here, it means it should be added to the above groups
  ],
};

const isKeyOfServiceGroupsBlueprint = (
  key: string,
  obj: ServiceGroupsBlueprint
): key is keyof ServiceGroupsBlueprint => {
  return key in obj;
};

export const getServiceGroups = (manifest: Record<string, string>) => {
  const result: ServiceGroupsBlueprint = {
    Activities: [],
    Commands: [],
    Console: [],
    Constraints: [],
    Dashboards: [],
    Events: [],
    Files: [],
    Infrastructure: [],
    Integrations: [],
    "Integration-testing": [],
    Jobs: [],
    Kafka: [],
    Metrics: [],
    Monitors: [],
    Orbits: [],
    Passes: [],
    Plans: [],
    Plugins: [],
    Procedures: [],
    Route: [],
    Records: [],
    Requirements: [],
    Requests: [],
    Resources: [],
    Streams: [],
    Synoptics: [],
    "Teams and Roles": [],
    Tenant: [],
    "Tenant Data Usage": [],
    Topology: [],
    Ungrouped: [],
  };

  Object.entries(manifest).forEach(([repoName]) => {
    for (const [group, repos] of Object.entries(serviceGroupsBlueprint)) {
      if (repos.includes(repoName)) {
        result[group as keyof ServiceGroupsBlueprint].push(repoName);
        return;
      }
    }
    result.Ungrouped.push(repoName);
  });
  // Sort the result to show groups in alphabetical order based on group name
  const sortedResult = Object.keys(result)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, key) => {
      if (isKeyOfServiceGroupsBlueprint(key, result)) {
        acc[key] = result[key];
      }
      return acc;
    }, {} as ServiceGroupsBlueprint);

  return sortedResult;
};
